/** @jsx jsx */
import {
  Button,
  Grid,
  Icon,
  Modal,
  Spacer,
  Spinner,
  Table,
  TextGroupMedium,
  useAsyncEffect,
} from "@cd2/components"
import { useFileboy } from "@cd2/fileboy-react"
import { Fields } from "@cd2/forms"
import { H3, P, TextWrap } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"
import { DownloadLinkButton } from "../Components/Buttons/DownloadLink"
import { ConfirmCancelDialogue } from "../Components/ConfirmCancelDialogue"
import { useEnsureGlobalState, useGlobalState } from "../Components/GlobalStateProvider"
import { ControlledPageTabs } from "../Components/PageTabs"
import { calculateCostPriceFromCurrency } from "../stories/quoteOrders/ItemRowSageProduct"
import {
  calculateDiscountPercent,
  calculateDiscountPrice,
  calculateMarginFromPrice,
  IItem,
  IQuoteOrderRecord,
} from "../stories/quoteOrders/quoteOrderUtils"

import { useTable } from "@cd2/components/build/es6/table/useTable"
import { ButtonsWrapper } from "../Components/ButtonsWrapper"
import { FieldsRadioButtonSelect } from "../Components/FieldsRadioCheckbox"
import { IRecord } from "../types/schema"
import { convertPriceCurrency } from "../utils/currency"
import {
  getFilteredProductLocations,
  getProductLocationPrioritized,
} from "../utils/getProductLocations"
import { getProductSuggestedPrice } from "../utils/getProductSuggestedPrice"
import { productDpdFilter } from "../utils/productDpdFilter"
import { useSettings } from "../utils/useSettings"
import { useProductGroupProductPrice } from "./productGroupProductPrice"
import { useConfirmation } from "./useConfirmation"
import { ConfirmAlternateProductModal, useProductAlternative } from "./useProductAlternative"
import { icons } from "../constants/icons"
import { brandColors } from "../Components/EOSThemeProvider"
import { QuickToolTip } from "../Components/QuickTooltip"
import { assert } from "../utils/utils"

type IKitItem = IRecord<
  "kit_items",
  "quantity" | "body" | "weight" | "type" | "optional" | "favourite"
> & {
  product_master?:
    | (IRecord<"product_masters", "stock_code" | "name"> & { products: IRecord<"products">[] })
    | null
}
export type IKitFile = IRecord<
  "files",
  "optional_on_kit" | "fileName" | "fileboyId" | "notes" | "created_at"
>
type IQuoteKit = IRecord<"kits", "name" | "comment_line"> & {
  created_by: IRecord<"skeem_users", "name">
  files: IKitFile[]
  items: IKitItem[]
  subKits: IRecord<"kits", "name">[]
}

type ISelectionType = "include" | "optional" | null
type IKitResponse = {
  action: "cancel" | "next" | "finalize" | "previous"
  data?: {
    comment_line?: string
    items: IKitItem[]
    itemIds: Record<string, ISelectionType>
    files: IKitFile[]
    fileIds: Record<string, ISelectionType>
  }
}

export interface IKitBuilderRow {
  item: Partial<IItem>
  callback: (itemId: string) => void
  optional: boolean
}

export function useQuoteKitBuilder(
  quote: IQuoteOrderRecord,
  actions: {
    onAddItem: (item: IKitBuilderRow) => void
    onAddFiles: (files: IKitFile[]) => void
  },
) {
  const {
    defaultStockLocation,
    barScanOrders,
    warehouses,
    showDiscountOption,
    dpdProductPrefixes,
    stockWarehouses,
    useExplicitAllocations,
    _loaded,
    useQuoteOrderStandardPricing,
    directDeliveryWarehouse,
    ...settings
  } = useSettings()
  const { currentUser } = useEnsureGlobalState()

  const skeem = window.skeem
  const fileboy = useFileboy()

  const checkProductForAlternative = useProductAlternative()
  const getGroupCostPrice = useProductGroupProductPrice()

  function setWindowCustomerPrices(key: string, data: any) {
    ;(window as any).customerPrices ||= {}
    ;(window as any).customerPrices[key] = data
  }

  const [confirmAlternativeProductModal, confirmAlternativeProduct] = useConfirmation<
    any,
    { id: string }
  >(({ onResolve, data }) => <ConfirmAlternateProductModal data={data} onResolve={onResolve} />)

  async function loadProductDataForSageItemRow(
    pid: string,
  ): Promise<{ item: Partial<IItem>; callback: (itemId: string) => void }> {
    let productId = pid
    const altProduct = await checkProductForAlternative(productId)
    if (altProduct.alternative) {
      const selected = await confirmAlternativeProduct(altProduct)
      productId = selected.id
    }
    const dpdProductFilter = productDpdFilter(dpdProductPrefixes ?? "")

    if (!productId) {
      await skeem.mutate({
        alerts: {
          create: {
            type: "useQuoteKitBuilder: No product ID selected",
            body: `No product ID selected`,
            details: { pid, altProduct: altProduct },
            notifications: ["support@cd2solutions.co.uk", "henry@cd2solutions.co.uk"],
          },
        },
      })
    }
    assert(productId, "useBuildSetBuilder: No product ID selected")

    type IProduct = IRecord<
      "products",
      | "stock_code"
      | "body"
      | "category"
      | "cost_price_pennies"
      | "line_item_description"
      | "sale_price_pennies"
      | "memo"
      | "price_updated_at"
      | "sage_id"
      | "is_consignment"
      | "original_cost_price_pennies"
      | "minimum_selling_price_pennies"
      | "minimum_margin"
      | "preferred_supplier_list_price_pennies"
      | "active"
    > & {
      branch: IRecord<"branches", "name" | "consignmentWarehouse" | "currency_name">
      product_locations: IRecord<
        "product_locations",
        "name" | "free_stock" | "warehouse" | "specific_location"
      >[]
      suppliers: IRecord<"suppliers", "currency_name">[]
      preferred_supplier: IRecord<"suppliers", "currency_name">
      product_group: IRecord<
        "product_groups",
        "products_price_method" | "memo" | "description" | "code" | "minimum_product_margin"
      >
    }
    const productFetch = skeem.fetchOne<IProduct>({
      products: {
        attributes: [
          "is_consignment",
          "stock_code",
          "body",
          "category",
          "cost_price_pennies",
          "line_item_description",
          "sale_price_pennies",
          "memo",
          "price_updated_at",
          "sage_id",
          "original_cost_price_pennies",
          "minimum_margin",
          "minimum_selling_price_pennies",
          "preferred_supplier_list_price_pennies",
          "active",
          { name: "branch", attributes: ["name", "consignmentWarehouse", "currency_name"] },
          { name: "suppliers", attributes: ["currency_name"] },
          { name: "preferred_supplier", attributes: ["currency_name"] },
          {
            name: "product_group",
            attributes: [
              "products_price_method",
              "memo",
              "description",
              "code",
              "minimum_product_margin",
            ],
          },
          {
            name: "product_locations",
            attributes: ["name", "free_stock", "warehouse", "specific_location"],
          },
        ],
        filter: {
          and: [
            { matches: { id: productId } },
            barScanOrders && dpdProductFilter.and.length > 0 && dpdProductFilter,
          ].filter(Boolean),
        },
      },
    })

    let customerPrice = undefined
    let customerPriceDiscountPercent = undefined

    const pricing = await getProductSuggestedPrice(skeem, quote, productId)
    console.log("load OG COST", pricing)
    customerPrice = pricing?.customerPrice
    customerPriceDiscountPercent = pricing?.customerPriceDiscountPercent
    customerPrice = convertPriceCurrency(
      customerPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )

    const product = await productFetch

    const locations = product.product_locations

    const productLocation = getProductLocationPrioritized(
      getFilteredProductLocations(locations),
      {
        defaultStockLocation: currentUser?.defaultWarehouse?.name ?? defaultStockLocation,
        branch: currentUser.branch,
        warehouses,
        defaultIntercoWarehouse: settings.defaultIntercoWarehouse,
        directDelivery: !!quote.delivery_method?.match(/direct/i),
        directDeliveryWarehouse: directDeliveryWarehouse,
      },
      { isConsignment: product.is_consignment, isInterco: quote.customer?.internalCustomer },
    )

    const useBody = !product.line_item_description

    const groupPrice = await getGroupCostPrice(product.id)
    const costPricePennies = calculateCostPriceFromCurrency({
      currency_name: quote.currency_name || currentUser.branch.currency_name,
      currentUser,
      groupPrice,
      product,
    })
    const standardPriceCurrencyConverted = convertPriceCurrency(
      pricing?.standardPrice ?? null,
      currentUser.branch.euro_pound_exchange_rate,
      pricing?.currency_name,
      quote.currency_name,
    )

    const isHikKeyAccount = !!(
      pricing?.applyOpportunityCertificateBreakdown &&
      quote.hasOpportunityCertificate &&
      !useQuoteOrderStandardPricing
    )

    const costPricePenniesUpdated = isHikKeyAccount
      ? calculateDiscountPrice(costPricePennies, 0)
      : costPricePennies

    const itemData: Partial<IItem> = {
      product,
      product_location: productLocation,
      stock_code: product.stock_code,
      stock_location: productLocation?.name ?? "",
      body: useBody
        ? groupPrice?.body ?? product.body
        : groupPrice?.line_item_description ?? product.line_item_description,
      category: product.category,
      cost_price_pennies: costPricePenniesUpdated,
      price_pennies: customerPrice || null,
      markUp: calculateMarginFromPrice(
        costPricePenniesUpdated || undefined,
        customerPrice ?? undefined,
      ),
      fixed_discount_rate: customerPriceDiscountPercent ?? null,
      standard_price_pennies: useQuoteOrderStandardPricing
        ? standardPriceCurrencyConverted
        : undefined,
      standard_discount_percent: useQuoteOrderStandardPricing
        ? calculateDiscountPercent(standardPriceCurrencyConverted, customerPrice)
        : undefined,
    }

    return {
      item: itemData,
      callback: (itemId: string) => setWindowCustomerPrices(itemId, pricing),
    }
  }

  async function prepareProductItem(item: IKitItem, optional: boolean) {
    if (!item.product_master?.id) {
      return null
    }
    const product = item.product_master.products[0]
    if (!product) {
      console.log("PREPARE ITEM - no product", item)
      return null
    }
    const itemData = await loadProductDataForSageItemRow(product.id)

    return {
      callback: itemData.callback,
      item: {
        ...itemData.item,
        quantity: item.quantity,
        allocated_quantity: item.quantity,
      },
      optional: optional,
    }
  }

  const [selectAKitModal, handleSelectAKit] = useConfirmation<
    IRecord<"kits", "subKits" | "name">[],
    string | null
  >(({ data, onResolve }) => {
    const [selectedKit, setSelectedKit] = useState<(typeof data)[number] | null>(null)
    return (
      <ConfirmCancelDialogue
        heading="Select a set"
        onConfirm={() => onResolve(selectedKit?.id ?? null)}
        onCancel={() => onResolve(null)}
        width={900}
        noOverflow
        isOpen
      >
        <Fields.Select2
          options={data}
          getOptionLabel={opt => opt.name}
          getOptionValue={opt => opt.id}
          onChange={value => setSelectedKit(value)}
          formatOptionLabel={(opt, ctx) => {
            const inMenuSelected =
              ctx.context == "menu" && (ctx.selectValue as any)?.[0]?.id == opt.id
            return (
              <TextWrap blockSpacing={0} color={inMenuSelected ? "white" : undefined}>
                <P>{opt.name}</P>
                {opt.subKits && opt.subKits.length > 0 && (
                  <P size="small">Children: {opt.subKits.length}</P>
                )}
              </TextWrap>
            )
          }}
          value={selectedKit}
        />
        <Spacer />
      </ConfirmCancelDialogue>
    )
  })

  const [manageSetModal, handleManageSet] = useConfirmation<
    {
      kit: IQuoteKit & { hasChain?: boolean }
      nextKit?: { id: string; name: string }
      pagination?: { index: number; last: number }
      previousResponse?: IKitResponse
    },
    IKitResponse
  >(({ data: { kit: data, nextKit, pagination, previousResponse }, onResolve }) => {
    // optional items that should be included
    const [favouritesFilter, setFavouritesFilter] = useState<boolean | null>(null)
    const [kitItems, setKitItems] = useState(previousResponse?.data?.items || data.items)
    const filteredItems = kitItems.filter(x =>
      favouritesFilter == null ? true : x.favourite == favouritesFilter,
    )
    function handleChangeQuantity(id: string, quantity?: number | null) {
      setKitItems(prev =>
        prev.map(item => {
          if (item.id === id) {
            return { ...item, quantity: quantity || null }
          }
          return item
        }),
      )
    }
    const hasPreviousKit = (pagination?.index ?? 0) > 0
    const [itemIds, setItemIds] = useState<Record<string, ISelectionType>>(
      previousResponse?.data?.itemIds ??
        kitItems.reduce(
          (acc, { id, optional }) => ({ ...acc, [id]: optional ? "optional" : null }),
          {} as Record<string, ISelectionType>,
        ),
    )
    function setPartialItemIds(ids: string[], type: ISelectionType) {
      setItemIds(prev => ({
        ...prev,
        ...ids.reduce((acc, id) => ({ ...acc, [id]: type }), {}),
      }))
    }
    const [fileIds, setFileIds] = useState<Record<string, ISelectionType>>(
      previousResponse?.data?.fileIds ??
        data.files.reduce(
          (acc, { id, optional_on_kit }) => ({
            ...acc,
            [id]: optional_on_kit ? null : "include",
          }),
          {} as Record<string, ISelectionType>,
        ),
    )

    // main tab // items or files
    const [openTab, setOpenTab] = useState<"items" | "files">("items")

    function handleCancel() {
      onResolve({ action: "cancel" })
    }

    function handleProceed(type: "next" | "finalize" | "previous") {
      const finalCopy = {
        action: type,
        data: {
          comment_line: data.comment_line,
          items: kitItems,
          itemIds: itemIds,

          files: data.files,
          fileIds: fileIds,
        },
      }
      console.log("KIT RESPONSE", data)
      onResolve(finalCopy)
    }

    function toggleId(
      setter: React.Dispatch<React.SetStateAction<Record<string, ISelectionType>>>,
      id: string,
      type: ISelectionType,
    ) {
      setter(prev => {
        if (prev[id] === type) {
          console.log("Clear type", id, type)
          return { ...prev, [id]: null }
        }
        console.log("set type", id, type)
        return { ...prev, [id]: type }
      })
    }

    function flattenToType(ids: string[], type: ISelectionType) {
      return ids.reduce((acc, id) => ({ ...acc, [id]: type }), {})
    }

    return (
      <Modal
        width={900}
        render={() => (
          <div>
            {hasPreviousKit && (
              <React.Fragment>
                <Button
                  leftIcon={icons.chevronDoubleLeft}
                  buttonColor="white"
                  onClick={() => handleProceed("previous")}
                >
                  Previous Set
                </Button>
                <Spacer />
              </React.Fragment>
            )}
            <Grid gap={16}>
              <TextGroupMedium
                heading={data.name}
                body={
                  data.comment_line ? (
                    <div>
                      <Spacer size={4} />
                      <P weight={600}>Comments:</P>
                      <div
                        css={css`
                          whitespace: pre-wrap;
                        `}
                      >
                        {data.comment_line}
                      </div>
                    </div>
                  ) : undefined
                }
              />

              <ControlledPageTabs
                onTabChange={setOpenTab}
                tabs={[
                  { tabKey: "items", children: "Items", icon: "", disabled: !kitItems.length },
                  { tabKey: "files", children: "Files", icon: "", disabled: !data.files.length },
                ]}
                selectedTab={openTab}
              />
              <div
                css={css`
                  .col-small {
                    min-width: 1px;
                    width: 0%;
                  }
                  .col1 {
                    min-width: 105px;
                    width: 0%;
                  }

                  .col2 {
                    min-width: 178px;
                    width: 0%;
                  }

                  .col-wide {
                    width: 100%;
                  }
                `}
              >
                {openTab == "items" && (
                  <React.Fragment>
                    <Fields.Select2
                      label="Filter favourites"
                      options={[
                        { label: "All", value: null },
                        { label: "Preferred", value: true },
                        { label: "Unpreferred", value: false },
                      ]}
                      onChange={opt => setFavouritesFilter(opt.value)}
                      value={[
                        { label: "All", value: null },
                        { label: "Preferred", value: true },
                        { label: "Unpreferred", value: false },
                      ].find(x => x.value === favouritesFilter)}
                    />
                    <Spacer />
                    <ButtonsWrapper>
                      <Button
                        onClick={() =>
                          setPartialItemIds(
                            filteredItems
                              .filter(x => x.product_master && x.product_master.products.length > 0)
                              .map(item => item.id),
                            "include",
                          )
                        }
                        extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                      >
                        Add all items
                      </Button>
                      <Button
                        onClick={() =>
                          setPartialItemIds(
                            filteredItems
                              .filter(x => x.product_master && x.product_master.products.length > 0)
                              .map(item => item.id),
                            "optional",
                          )
                        }
                        extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                      >
                        Add all items as optional
                      </Button>
                      <Button
                        onClick={() =>
                          setPartialItemIds(
                            filteredItems.map(item => item.id),
                            null,
                          )
                        }
                        extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                      >
                        Remove all items
                      </Button>
                    </ButtonsWrapper>
                    <Spacer />
                    <div
                      css={css`
                        .no-product {
                          background-color: ${brandColors.redLightBackground};
                        }
                      `}
                    >
                      <Table
                        data={filteredItems}
                        rowClass={row =>
                          row.product_master && !row.product_master.products[0] ? "no-product" : ""
                        }
                      >
                        <CustomItemTableRow
                          itemIds={itemIds}
                          toggleId={(id, type) => toggleId(setItemIds, id, type)}
                          handleChangeQuantity={handleChangeQuantity}
                        />
                      </Table>
                    </div>
                  </React.Fragment>
                )}

                {openTab == "files" && (
                  <React.Fragment>
                    <ButtonsWrapper>
                      <Button
                        onClick={() =>
                          setFileIds(
                            flattenToType(
                              data.files.map(item => item.id),
                              "include",
                            ),
                          )
                        }
                        extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                      >
                        Add all items
                      </Button>
                      <Button
                        onClick={() =>
                          setFileIds(
                            flattenToType(
                              data.files.map(item => item.id),
                              null,
                            ),
                          )
                        }
                        extendTheme={{ padding: { sides: 10, topAndBottom: 8 } }}
                      >
                        Remove all items
                      </Button>
                    </ButtonsWrapper>
                    <Spacer />
                    <Table data={data.files}>
                      <Table.Column headingClassName="col1">
                        {row => (
                          <FieldsRadioButtonSelect
                            name={`include-${row.id}`}
                            onChange={() => toggleId(setFileIds, row.id, "include")}
                            value={fileIds[row.id]}
                            options={[{ label: "Include", value: "include" }]}
                            clearOnReselect
                          />
                        )}
                      </Table.Column>
                      <Table.DataColumn heading="File" field="fileName" />
                      <Table.Column heading="File">
                        {row => (
                          <DownloadLinkButton
                            to={fileboy.buildFileUrl(row.fileboyId, { download: row.fileName })}
                          >
                            Download
                          </DownloadLinkButton>
                        )}
                      </Table.Column>
                    </Table>
                  </React.Fragment>
                )}
              </div>
              <Grid columns={["max-content", "max-content", "auto", "max-content"]} gap={8}>
                <div>
                  <Button onClick={() => handleProceed("finalize")}>Finalize</Button>
                </div>
                {nextKit ? (
                  <div>
                    <Button onClick={() => handleProceed("next")} buttonStyle="outline">
                      Next Quote Set
                      {pagination ? (
                        <span>
                          {" "}
                          ({pagination.index + 1}/{pagination.last})
                        </span>
                      ) : null}
                    </Button>
                  </div>
                ) : (
                  <div />
                )}
                <div />
                <Button onClick={handleCancel} buttonColor="white">
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </div>
        )}
        isOpen
      />
    )
  })

  async function loadQuoteKit(id: string) {
    const kit = await skeem.fetch2<IQuoteKit & { hasChain?: boolean }>({
      first: {
        name: "kits",
        attributes: [
          "name",
          "comment_line",
          { name: "created_by", attributes: ["name"] },
          {
            name: "files",
            attributes: ["optional_on_kit", "fileName", "fileboyId", "notes", "created_at"],
          },
          {
            name: "items",
            attributes: [
              "quantity",
              "body",
              "weight",
              "type",
              "optional",
              "favourite",
              {
                name: "product_master",
                attributes: [
                  "stock_code",
                  "name",
                  { name: "products", filter: { matches: { branch: currentUser.branch.id } } },
                ],
              },
            ],
            sort: { by: "weight", direction: "asc" },
          },
          { name: "subKits", attributes: ["name"], sort: { by: "created_at", direction: "asc" } },
        ],
        matches: { id },
      },
    })
    return kit
  }

  async function handleBuildQuoteKit(branchId: string) {
    const kits = await skeem.fetch2<IRecord<"kits", "name" | "subKits">[]>({
      model: {
        name: "kits",
        attributes: ["name", { name: "subKits", sort: { by: "created_at", direction: "asc" } }],
        filter: { matches: { branch: branchId } },
      },
    })
    console.log("LOADED KITS", kits)
    const selectedKitId = await handleSelectAKit(kits)
    if (!selectedKitId) {
      return null
    }
    const kitResponses: IKitResponse[] = []

    // run the first kit / parent kit manually
    const firstKit = await loadQuoteKit(selectedKitId)
    const initialResponse = await handleManageSet({
      kit: firstKit,
      nextKit: firstKit.subKits[0],
      pagination: { index: 0, last: firstKit.subKits.length + 1 }, // add one so it includes itself
    })
    // handle the response & recursively handle the next kits
    const responses = await handleKitResponse(
      { kit: firstKit, chain: [firstKit, ...firstKit.subKits], index: 0 },
      {
        result: initialResponse,
        responses: kitResponses,
      },
    )
    if (responses) {
      // process responses to produce items & files for quote
      return handleDataPrep(responses)
    }
    return null
  }

  async function handleKitResponse(
    data: { kit: IQuoteKit; chain: IRecord<"kits", "name">[]; index: number },
    response: { result: IKitResponse; responses: IKitResponse[] },
  ) {
    console.log("HANDLE KIT RESPONSE", data, response)
    const { chain, index } = data

    const nextKit = chain[index + 1]
    const previousKit = chain[index - 1]
    const currentKit = chain[index]

    const { result, responses } = response
    console.log("--KIT ACTION--", index, result.action)

    // If the user has walked back in the flow, then clicked finalize. Check before proceeding, as we will
    // remove any data after the current step. If they don't confirm, change the action to next instead.
    if (
      result.action === "finalize" &&
      responses[index + 1] &&
      !confirm(
        `You have already completed the steps after this point. If you proceed, the data from those steps will be lost. Do you want to continue?`,
      )
    ) {
      result.action = "next"
    }

    // abort flow
    if (result.action === "cancel") {
      console.log("KIT CANCEL")
      return null
    }

    // push in result
    responses[index] = result

    if (result.action === "previous") {
      const previousResponse = responses[index - 1]
      const loadedPreviousKit = await loadQuoteKit(previousKit.id)
      console.log("SHOW NEXT KIT", {
        kit: loadedPreviousKit,
        nextKit: currentKit,
        pagination: { index: index + 1, last: chain.length },
        previousResponse,
      })
      // rendering the next kit form
      const nextKitResponse = await handleManageSet({
        kit: loadedPreviousKit,
        nextKit: currentKit,
        pagination: { index: index - 1, last: chain.length },
        previousResponse,
      })
      // handling that kits forms result
      return await handleKitResponse(
        { kit: loadedPreviousKit, chain, index: index - 1 },
        { result: nextKitResponse, responses },
      )
    }

    if (result.action === "next") {
      const loadedNextKit = await loadQuoteKit(nextKit.id)
      console.log("SHOW NEXT KIT", {
        kit: loadedNextKit,
        nextKit: chain[index + 2], // this is the kit AFTER the one we want to display
        pagination: { index: index + 1, last: chain.length },
      })
      // rendering the next kit form
      const nextKitResponse = await handleManageSet({
        kit: loadedNextKit,
        // this is the kit AFTER the one we want to display
        nextKit: chain[index + 2],
        pagination: { index: index + 1, last: chain.length },
        previousResponse: responses[index + 1],
      })
      return await handleKitResponse(
        { kit: loadedNextKit, chain, index: index + 1 },
        { result: nextKitResponse, responses },
      )
    }

    // if finalize, return stored responses up to the current index
    if (result.action === "finalize") {
      const finalResponse = responses.slice(0, index + 1)
      console.log("KIT FINALIZE", responses, finalResponse)
      if (index < chain.length - 1) {
        return finalResponse
      }
      return finalResponse
    }
    console.log("NO KIT ACTION?", result.action)
    return null
  }

  const [prepareDataModal, handleDataPrep] = useConfirmation<IKitResponse[], boolean>(
    ({ data, onResolve }) => {
      async function handlePrepareData() {
        try {
          for (const quoteSet of data) {
            const ids = quoteSet.data?.itemIds ?? {}

            if (quoteSet.data?.comment_line) {
              actions.onAddItem({
                item: {
                  type: 1,
                  body: quoteSet.data.comment_line,
                  quantity: 1,
                  allocated_quantity: 1,
                },
                callback: () => {},
                optional: false,
              })
            }
            for (const item of quoteSet.data?.items ?? []) {
              if (ids[item.id] === "include" || ids[item.id] === "optional") {
                const preparedItem = await prepareProductItem(item, ids[item.id] === "optional")
                if (preparedItem) {
                  // override whatever the quantity is with what it was set to in the response data
                  preparedItem.item.quantity = item.quantity
                  actions.onAddItem(preparedItem)
                }
              }
            }
          }

          const flatFiles: IKitFile[] = []
          data.forEach(x => {
            const ids = x.data?.fileIds ?? {}

            x.data?.files.forEach(item => {
              if (ids[item.id] === "include" || ids[item.id] === "optional") {
                flatFiles.push({ ...item, optional_on_kit: ids[item.id] === "optional" })
              }
            })
          })

          actions.onAddFiles(flatFiles)
          onResolve(true)
        } catch (e) {
          console.error(e)
          onResolve(false)
        }
      }

      useAsyncEffect(handlePrepareData, [])
      return (
        <Modal
          render={() => (
            <div>
              <Spacer />
              <H3 align="center">Processing changes</H3>
              <Spacer />
              <P align="center">Please wait for the items to be loaded.</P>
              <P align="center">This modal will close automatically once done.</P>
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                `}
              >
                <Spinner size={64} color="black" />
              </div>
              <Spacer />
            </div>
          )}
          isOpen
        />
      )
    },
  )

  return {
    handleBuildQuoteKit,
    modals: (
      <React.Fragment key="kit-loader-modals">
        {selectAKitModal}
        {manageSetModal}
        {confirmAlternativeProductModal}
        {prepareDataModal}
      </React.Fragment>
    ),
  }
}

function CustomItemTableRow({
  toggleId,
  itemIds,
  handleChangeQuantity,
}: {
  toggleId: (id: string, type: ISelectionType) => void
  itemIds: Record<string, ISelectionType>
  handleChangeQuantity: (id: string, quantiy?: number | null) => void
}) {
  const table = useTable()
  if (table.isHeading) {
    return (
      <React.Fragment>
        <Table.Column headingClassName="col-small">{() => null}</Table.Column>
        <Table.Column headingClassName="col1">{() => null}</Table.Column>
        <Table.Column headingClassName="col2">{() => null}</Table.Column>
        <Table.Column heading="Item">{() => null}</Table.Column>
        <Table.Column heading="Quantity">{() => null}</Table.Column>
      </React.Fragment>
    )
  }
  const row = table.row as IKitItem
  if (row.type === "text") {
    return (
      <td
        colSpan={4}
        css={css`
          padding: 8px 14px;
        `}
      >
        <b>{row.body}</b>
      </td>
    )
  }
  return (
    <React.Fragment>
      <Table.Column headingClassName="col-small">
        {row =>
          row.product_master && !row.product_master.products[0] ? (
            <div
              css={css`
                min-width: 16px;
              `}
            >
              <QuickToolTip content="Your branch is missing a product for this item">
                <Icon icon={icons.alert} color={brandColors.red} size={18} />
              </QuickToolTip>
            </div>
          ) : row.favourite ? (
            <div
              css={css`
                min-width: 16px;
              `}
            >
              <Icon icon={icons.favourite} color={brandColors.darkMint} size={18} />
            </div>
          ) : null
        }
      </Table.Column>
      <Table.Column headingClassName="col1">
        {row =>
          row.type == "text" ? null : (
            <FieldsRadioButtonSelect
              name={`include-${row.id}`}
              onChange={() => toggleId(row.id, "include")}
              value={itemIds[row.id]}
              options={[
                {
                  label: "Include",
                  value: "include",
                  disabled: row.product_master && !row.product_master.products[0],
                },
              ]}
              clearOnReselect
            />
          )
        }
      </Table.Column>
      <Table.Column headingClassName="col2">
        {row =>
          row.type == "text" ? null : (
            <FieldsRadioButtonSelect
              name={`include-${row.id}`}
              onChange={() => toggleId(row.id, "optional")}
              value={itemIds[row.id]}
              options={[
                {
                  label: "Include as optional",
                  value: "optional",
                  disabled: row.product_master && !row.product_master.products[0],
                },
              ]}
              clearOnReselect
            />
          )
        }
      </Table.Column>
      <Table.Column heading="Item">
        {row => {
          if (row.type == "text") {
            return <b>{row.body}</b>
          }
          return (
            <TextWrap blockSpacing={0}>
              <P>{row.product_master?.stock_code}</P>
              <P>{row.product_master?.name}</P>
            </TextWrap>
          )
        }}
      </Table.Column>
      <Table.Column heading="Quantity">
        {row =>
          row.type === "text" ? null : (
            <Fields.Number2
              onChange={qty => handleChangeQuantity(row.id, qty)}
              value={row.quantity}
              disabled={row.product_master && !row.product_master.products[0]}
            />
          )
        }
      </Table.Column>
    </React.Fragment>
  )
}
